import { useState } from 'react';
import PropTypes from 'prop-types';

// material-ui
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

// project imports
import { handlerSidebarMode, useGetMenuMaster } from 'api/menu';

// assets
import { IconRoomPreferences, IconSidebarAuto, IconSidebarUser } from 'utils/icons';

// ==============================|| SIDEBAR MODE SWITCH (AUTO / UŽIVATEL) ||============================== //
// "Auto" = sidebar se dál sám sbaluje/rozbaluje podle šířky okna
// (Dashboard/index.jsx's downXL efekt, původní chování). "Uživatel" = ten
// efekt se přestane pletl do stavu, sidebar zůstane přesně tak, jak si ho
// uživatel naposledy ručně nastavil, i po zmenšení okna - stav se pamatuje
// přes reload (api/menu.js, localStorage).
//
// Otevřený sidebar: vodorovný ToggleButtonGroup se dvěma ikonami - stejný
// vizuální vzor jako ThemeModeSwitch (světlé/tmavé/systém).
//
// Úzký (staženy) sidebar (2026-08-23, na přání uživatele): dvě vedle sebe
// ikony by se do 60px lišty nevešly čitelně - místo toho jedna ikona
// RoomPreferences, po kliknutí Menu (otevírá se nahoru/doprava od ikony,
// je úplně dole v appce) se dvěma položkami, každá s vlastní ikonou i
// popiskem.

const MODES = [
  { value: 'auto', icon: IconSidebarAuto, label: 'Automaticky', title: 'Automaticky podle šířky okna' },
  { value: 'user', icon: IconSidebarUser, label: 'Uživatelem', title: 'Vždy podle mého ručního nastavení' }
];

export default function SidebarModeSwitch({ open }) {
  const { menuMaster } = useGetMenuMaster();
  const mode = menuMaster?.sidebarMode || 'auto';
  const [anchorEl, setAnchorEl] = useState(null);

  const handleChange = (event, newMode) => {
    if (newMode !== null) handlerSidebarMode(newMode);
  };

  const handleMenuSelect = (value) => {
    handlerSidebarMode(value);
    setAnchorEl(null);
  };

  if (!open) {
    return (
      <Stack sx={{ alignItems: 'center' }}>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="chování sidebaru"
          title="Chování sidebaru"
        >
          <IconRoomPreferences style={{ fontSize: '1.3rem' }} />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{ list: { sx: { py: 0 } } }}
        >
          <ListSubheader sx={{ minHeight: 40, lineHeight: '40px', fontSize: '0.75rem' }}>Postranní panel</ListSubheader>
          {MODES.map(({ value, icon: Icon, label, title }) => (
            <MenuItem key={value} selected={mode === value} onClick={() => handleMenuSelect(value)} title={title} sx={{ minHeight: 40 }}>
              <ListItemIcon>
                <Icon style={{ fontSize: '1.3rem' }} />
              </ListItemIcon>
              <ListItemText primary={label} />
            </MenuItem>
          ))}
        </Menu>
      </Stack>
    );
  }

  return (
    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
      <Stack sx={{ lineHeight: 0 }}>
        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem', lineHeight: 1.2, fontWeight: 500, color: 'text.secondary' }}>
          Postranní
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem', lineHeight: 1.2, fontWeight: 500, color: 'text.secondary' }}>
          Panel
        </Typography>
      </Stack>
      <ToggleButtonGroup
        value={mode}
        exclusive
        size="small"
        orientation="horizontal"
        onChange={handleChange}
        aria-label="chování sidebaru"
        sx={{
          gap: 1,
          '& .MuiToggleButtonGroup-grouped': {
            margin: 0,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            color: 'text.secondary',
            '&.Mui-selected': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'transparent' },
            '&:hover': { borderColor: 'primary.light', bgcolor: 'transparent' }
          }
        }}
      >
        {MODES.map(({ value, icon: Icon, label, title }) => (
          <ToggleButton key={value} value={value} aria-label={label} title={title}>
            <Icon style={{ fontSize: '1.2rem' }} />
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
}

SidebarModeSwitch.propTypes = { open: PropTypes.bool };
